import { useState } from 'react';
import { HiOutlineDownload, HiOutlineEye } from 'react-icons/hi';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  formatDate,
  formatNumber,
  ImageViewer,
  StatusField,
  TextAreaInput,
} from '@amfintech/react-admin-ui';
import { createHelpers, createResource, DynamicForm, sentenceCase } from '@amfintech/refine-react-admin';
import { useOne } from '@refinedev/core';
import { useForm } from '@refinedev/react-hook-form';
import { capitalCase } from 'change-case';

import {
  AdminUser,
  AuthorisedSignatory,
  ModificationEntityType,
  OrderDocument,
  OrderDocumentStatus,
  OrderStatus,
  Payment,
  PaymentMethod,
  Signatory,
  SignatoryStatus,
  Transaction,
  TransactionStatus,
  TransactionType,
  type Order
} from '~/api';
import { useApproveOrderMutation } from '~/hooks/use-approve-order';
import { useCompleteManualProcessing } from '~/hooks/use-complete-manual-processing';
import { usePendingPaymentVerificationOrder } from '~/hooks/use-pending-payment-verification-order';
import { useRejectOrderMutation } from '~/hooks/use-reject-order';

import { resourceNames } from './resource-names';
import { formatTransactionStatus } from './transaction-resource';

const {
  defineFields,
  defineCardSection,
  defineShowPage,
  defineRelatedResourceSection,
} = createHelpers<Order>({
  resourceName: resourceNames.order,
});

export const formatOrderStatus = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Approved:
      return 'success';
    case OrderStatus.Pending:
      return 'info';
    case OrderStatus.PendingSubmission:
      return 'info';
    case OrderStatus.PendingPaymentVerification:
      return 'info';
    case OrderStatus.PendingSignature:
      return 'warn';
    case OrderStatus.PendingOpsConfirmation:
      return 'warn';
    case OrderStatus.Cancelled:
      return 'danger';
    case OrderStatus.Rejected:
      return 'danger';
    default:
      return 'pending';
  }
};

export const formatOrderStatusLabel = (status: OrderStatus) => {
  switch (status) {
    case OrderStatus.Created:
      return 'Created';
    case OrderStatus.Pending:
      return 'Pending';
    case OrderStatus.PendingSubmission:
      return 'Pending Submission';
    case OrderStatus.PendingPaymentVerification:
      return 'Pending Payment Verification';
    case OrderStatus.PendingSignature:
      return 'Pending Signature';
    case OrderStatus.PendingOpsConfirmation:
      return 'Pending Ops Confirmation';
    case OrderStatus.Approved:
      return 'Approved';
    case OrderStatus.Rejected:
      return 'Rejected';
    case OrderStatus.Cancelled:
      return 'Cancelled';
    case OrderStatus.Voided:
      return 'Voided';
    case OrderStatus.Completed:
      return 'Completed';
    default:
      return status;
  }
};

export const formatSignatoryStatus = (status: SignatoryStatus) => {
  switch (status) {
    case SignatoryStatus.Completed:
      return 'success';
    default:
      return 'pending';
  }
};

export const formatOrderDocumentStatus = (status: OrderDocumentStatus) => {
  switch (status) {
    case OrderDocumentStatus.Completed:
      return 'success';
    case OrderDocumentStatus.Declined:
      return 'danger';
    case OrderDocumentStatus.Voided:
      return 'warn';
    case OrderDocumentStatus.Sent:
      return 'info';
    default:
      return 'pending';
  }
};

export const handleDownload = (url: string, name: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = name || 'download';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

interface ActionDialogButtonProps {
  triggerText?: string;
  triggerIcon?: React.ReactNode;
  triggerVariant?: 'solid' | 'outline' | 'ghost';
  triggerSize?: 'icon' | 'default' | 'sm' | 'lg';
  triggerClassName?: string;
  dialogTitle: string;
  dialogDescription: string;
  actionText: string;
  cancelText: string;
  onAction: (remarks?: string) => void;
  needsRemarks?: boolean;
  disabled?: boolean;
}

const ActionDialogButton = ({
  triggerText,
  triggerVariant = 'solid',
  triggerSize = 'default',
  triggerClassName,
  dialogTitle,
  dialogDescription,
  actionText,
  cancelText,
  onAction,
  needsRemarks = false,
  disabled = false,
}: ActionDialogButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [remarks, setRemarks] = useState('');

  const handleAction = () => {
    onAction(needsRemarks ? remarks : undefined);
    setIsOpen(false);
    setRemarks('');
  };

  const handleCancel = () => {
    setIsOpen(false);
    setRemarks('');
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button
          variant={triggerVariant}
          size={triggerSize}
          className={triggerClassName}
          disabled={disabled}
          onClick={() => setIsOpen(true)}
        >
          {triggerText}
        </Button>
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay />
        <DialogContent>
          <DialogTitle>{dialogTitle}</DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
          {needsRemarks && (
            <div className="space-y-2">
              <label htmlFor="remarks" className="block text-sm font-medium text-gray-700">
                Remarks
              </label>
              <TextAreaInput
                id="remarks"
                name="remarks"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="solid" onClick={handleAction}>
              {actionText}
            </Button>
            <Button variant="outline" onClick={handleCancel}>
              {cancelText}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};

export const orderResource = createResource({
  name: resourceNames.order,
  label: 'Orders',
  fields: defineFields([
    'id',
    'referenceNo',
    'originId',
    'userId',
    {
      user: ['email', 'name'],
    },
    'status',
    'accountMasterAccountNo',
    {
      payments: ['referenceNo', 'amount', 'currency', 'transactedAt', 'paymentMethod', 'status'],
    },
    { transactions: [{account: [{ profile: ['idNo']}]}]},
    'requireManualProcessing',
    'transactionType',
    'createdAt',
    'updatedAt',
    'updatedById',
  ]),
  defaultValues: {},
  list: {
    filters: {
      collapsible: 'expand-by-default',
      initial: [
        {
          field: 'status',
          operator: 'eq',
          value: OrderStatus.PendingOpsConfirmation,
        },
      ],
    },
    makerChecker: {
      entityTypes: [ModificationEntityType.Order],
      activeTabFilters: [
        {
          field: 'status',
          operator: 'eq',
          value: OrderStatus.PendingOpsConfirmation,
        },
      ],
      additionalTabs: [
        {
          label: 'Pending Payment',
          value: 'pending-payment',
          filters: [
            {
              field: 'status',
              operator: 'eq',
              value: OrderStatus.PendingPaymentVerification,
            },
          ],
        },
      ],
    },
  },
  filterControls: {
    referenceNo: {
      type: 'text',
      config: {
        label: 'Order Ref. No.',
      },
      operator: 'contains',
    },
    createdAt: {
      type: 'daterange',
      config: {
        label: 'Date Range',
      },
    },
    status: {
      type: 'select',
      config: {
        label: 'Order Status',
        options: Object.entries(OrderStatus).map(([key, value]) => ({
          label: formatOrderStatusLabel(value),
          value: value,
        })),
      },
      operator: 'eq',
    },
    transactionType: {
      type: 'select',
      config: {
        label: 'Transaction Type',
        options: Object.entries(TransactionType).map(([key, value]) => ({
          label: key,
          value: value,
        })),
      },
      operator: 'eq',
    },
    accountMasterAccountNo: {
      type: 'text',
      config: {
        label: 'Master Account Number',
      },
      operator: 'contains',
    },
  },
  allowCreate: false,
  allowDownload: true,
  defaultSorter: [{ field: 'createdAt', order: 'desc' }],
  columns: ({ LinkToDetails }) => [
    {
      id: 'referenceNo',
      header: 'Reference Number',
      accessorKey: 'referenceNo',
      cell: (data) => {
        const refNumber = data.cell.getValue<string>();
        return (
          <LinkToDetails resourceId={data.row.original.id} className="font-semibold">
            {refNumber}
          </LinkToDetails>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (data) => {
        const status = data.cell.getValue<OrderStatus>();
        return <StatusField status={formatOrderStatus(status)} label={formatOrderStatusLabel(status)} />;
      },
    },
    {
      id: 'accountMasterAccountNo',
      header: 'Master Account Number',
      accessorKey: 'accountMasterAccountNo',
    },
    {
      id: 'transactionType',
      header: 'Transaction Type',
      accessorKey: 'transactionType',
    },
    {
      id: 'user.name',
      header: 'Name / ID No.',
      accessorKey: 'user.name',
      cell: (data) => {
        const name = data.cell.getValue<string>();
        const idNo = data.row.original.transactions?.[0]?.account?.profile?.idNo || '';
        return <>{name ? name : '-'} / {idNo ? idNo : '-'}</>;
      },
    },
    {
      id: 'createdAt',
      header: 'Created At',
      accessorKey: 'createdAt',
      cell: (data) => {
        const datetime = data.cell.getValue<Date>();
        return <>{formatDate(datetime, { formatType: 'dateAndTime' })}</>;
      },
    },
    {
      id: 'updatedAt',
      header: 'Updated At',
      accessorKey: 'updatedAt',
      cell: (data) => {
        const datetime = data.cell.getValue<Date>();
        return <>{formatDate(datetime, { formatType: 'dateAndTime' })}</>;
      },
    },
  ],
  allowEdit: false,
  allowDelete: false,
  show: defineShowPage({
    actions: (_, option) => {
      const { mutate: approveMutation } = useApproveOrderMutation();
      const { mutate: rejectMutation } = useRejectOrderMutation();
      const { mutate: completeManualProcessing } = useCompleteManualProcessing();
      const { mutate: markPendingPaymentVerification } = usePendingPaymentVerificationOrder();

              const ApproveButton = () => (
          <ActionDialogButton
            triggerText="Approve"
            triggerVariant="outline"
            triggerSize="default"
            triggerClassName="gap-2"
            dialogTitle="Order Confirmation"
            dialogDescription="Please confirm the transaction charges on the order. The update cannot be reverted once the order is submitted."
            actionText="Confirm"
            cancelText="Edit"
            onAction={(remarks) => approveMutation(option?.id, remarks)}
            needsRemarks
          />
        );

        const RejectButton = () => (
          <ActionDialogButton
            triggerText="Reject"
            triggerVariant="outline"
            triggerSize="default"
            triggerClassName="gap-2"
            dialogTitle="Order Rejection"
            dialogDescription="Please provide a reason for rejecting the order."
            actionText="Reject"
            cancelText="Cancel"
            onAction={(remarks) => rejectMutation(option?.id, remarks)}
            needsRemarks
          />
        );

      const ManualProcessButton = () => (
        <ActionDialogButton
          triggerText="Submit Manually"
          triggerVariant="outline"
          triggerSize="default"
          triggerClassName="gap-2"
          dialogTitle="Complete Manual Processing"
          dialogDescription="Please confirm the completion of the manual processing order."
          actionText="Confirm"
          cancelText="Cancel"
          onAction={(remarks) => completeManualProcessing(option?.id, remarks)}
          needsRemarks
        />
      );

      const PendingPaymentVerificationButton = () => (
        <ActionDialogButton
          triggerText="Pending Payment Verification"
          triggerVariant="outline"
          triggerSize="default"
          triggerClassName="gap-2"
          dialogTitle="Pending Payment Verification"
          dialogDescription="Please confirm that this order should be marked as pending payment verification."
          actionText="Confirm"
          cancelText="Cancel"
          onAction={() => markPendingPaymentVerification(option?.id)}
        />
      );

      return (
        <>
          {(option?.data?.status === OrderStatus.Pending ||
            option?.data?.status === OrderStatus.PendingOpsConfirmation ||
            option?.data?.status === OrderStatus.PendingPaymentVerification) &&
            !option?.modification && (
            <div className="flex gap-2">
              {option?.data?.status === OrderStatus.PendingOpsConfirmation &&
                option?.data?.transactionType === TransactionType.Buy && <PendingPaymentVerificationButton />}
              {option?.data?.requireManualProcessing ? <ManualProcessButton /> : <ApproveButton />}
              <RejectButton />
            </div>
            )}
        </>
      );
    },
    sections: [
      defineCardSection({
        title: 'Order Details',
        collapsible: 'expand-by-default',
        fields: [
          'referenceNo',
          'originId',
          'updatedById',
          'transactionType',
          'status',
          'remarks',
          'createdAt',
          'updatedAt',
        ],
        displays: {
          referenceNo: {
            label: 'Reference Number',
          },
          originId: {
            label: 'Origin ID',
          },
          updatedById: {
            label: 'Updated By',
            render: (data, modifications) => {
              const RenderUpdatedBy = () => {
                const { data: updatedBy } = useOne<AdminUser>({
                  resource: resourceNames.adminUser,
                  id: data.updatedById || '',
                  queryOptions: {
                    enabled: !!data.updatedById && !modifications,
                  },
                  meta: {
                    fields: ['username'],
                  },
                });

                if (modifications) {
                  return (
                    <modifications.ModificationDiff
                      oldValue={modifications.oldValues.updatedById}
                      newValue={modifications.newValues.updatedById}
                      getModifiedResource={{
                        resourceName: resourceNames.adminUser,
                        oldId: modifications.oldValues.updatedById,
                        newId: modifications.newValues.updatedById,
                        field: 'username',
                      }}
                    />
                  );
                }
                return updatedBy?.data?.username || 'SYSTEM';
              };

              return <RenderUpdatedBy />;
            },
          },
          status: {
            label: 'Status',
          },
          remarks: {
            label: 'Remarks',
          },
          createdAt: {
            label: 'Created At',
            type: 'date',
            formatType: 'dateAndTime',
          },
          updatedAt: {
            label: 'Updated At',
            type: 'date',
            formatType: 'dateAndTime',
          },
        },
      }),
      defineRelatedResourceSection<OrderDocument>({
        relatedResourceName: resourceNames.orderDocument,
        fields: ['id'],
        relatedResourceFields: [
          'id',
          'status',
          'referenceId',
          'documentType',
          'document',
          'orderId',
          {
            order: ['referenceNo'],
          },
        ],
        title: 'Documents',
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        columns: () => [
          {
            id: 'referenceId',
            header: 'Reference ID',
            accessorKey: 'referenceId',
            cell: (data) => {
              const referenceId = data.cell.getValue<string>();
              return referenceId ? referenceId : 'No Reference ID';
            },
          },
          {
            id: 'documentType',
            header: 'Type',
            accessorKey: 'documentType',
            cell: (data) => {
              return capitalCase(data.cell.getValue<string>());
            },
          },
          {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            cell: (data) => {
              const status = data.cell.getValue<OrderDocumentStatus>();
              return <StatusField status={formatOrderDocumentStatus(status)} label={status} />;
            },
          },
          {
            id: 'document',
            header: 'Document',
            accessorKey: 'document',
            cell: (data) => {
              const document = data.cell.getValue<string>();
              if (!document) return 'No Document';
              return (
                <Dialog>
                  <div className="flex gap-2">
                    <DialogTrigger asChild>
                      <Button variant={'solid'} size="sm" className="gap-2">
                        <HiOutlineEye className="h-4 w-4" />
                        View
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[80vh] max-w-[60vw] w-[fit-content] overflow-y-auto">
                      <ImageViewer src={document} alt="Document" />
                    </DialogContent>
                    <Button
                      variant={'solid'}
                      size="sm"
                      className="gap-2"
                      onClick={() =>
                        handleDownload(document, data.row.original.order.referenceNo || 'document')
                      }
                    >
                      <HiOutlineDownload className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </Dialog>
              );
            },
          },
        ],
        getFilterConfig: ({ id }) => ({
          permanent: [
            {
              field: 'orderId',
              operator: 'eq',
              value: id,
            },
          ],
        }),
      }),
      defineRelatedResourceSection<Transaction>({
        relatedResourceName: resourceNames.transaction,
        fields: ['id'],
        relatedResourceFields: [
          'id',
          'referenceNo',
          'transactionDatetime',
          'transactionType',
          'quantity',
          'navPrice',
          'amount',
          'transactionStatus',
          'transactionCharge',
          { order: ['status'] },
        ],
        title: 'Transactions',
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        columns: ({ LinkToDetails }) => [
          {
            id: 'referenceNo',
            header: 'Reference Number',
            accessorKey: 'referenceNo',
            cell: (data) => {
              const refNumber = data.cell.getValue<string>();
              return (
                <LinkToDetails className="font-semibold" resourceId={data.row.original.id}>
                  {refNumber}
                </LinkToDetails>
              );
            },
          },
          {
            id: 'transactionType',
            header: 'Transaction Type',
            accessorKey: 'transactionType',
          },
          {
            id: 'unit',
            header: 'Unit',
            accessorKey: 'quantity',
            cell: (data) => {
              const amount = data.cell.getValue<number>();
              return formatNumber(amount);
            },
          },
          {
            id: 'navPrice',
            header: 'Nav Price',
            accessorKey: 'navPrice',
            cell: (data) => {
              const amount = data.cell.getValue<number>();
              return formatNumber(amount, {
                minDecimal: 4,
                maxDecimal: 4,
              });
            },
          },
          {
            id: 'amount',
            header: 'Amount',
            accessorKey: 'amount',
            cell: (data) => {
              const amount = data.cell.getValue<number>();
              return formatNumber(amount);
            },
          },
          {
            id: 'transactionCharge',
            header: 'Transaction Charge',
            accessorKey: 'transactionCharge',
            cell: (data) => {
              const amount = data.cell.getValue<number>();
              return `${formatNumber(amount)}%`;
            },
          },
          {
            id: 'transactionStatus',
            header: 'Transaction Status',
            accessorKey: 'transactionStatus',
            cell: (data) => {
              const status = data.cell.getValue<TransactionStatus>();
              return <StatusField status={formatTransactionStatus(status)} label={status} />;
            },
          },
          {
            id: 'transactionDatetime',
            header: 'Transaction Datetime',
            accessorKey: 'transactionDatetime',
            cell: (data) => {
              const datetime = data.cell.getValue<Date>();
              return datetime ? formatDate(datetime, { formatType: 'dateAndTime' }) : '-';
            },
          },
          {
            id: 'actions',
            header: 'Actions',
            accessorKey: 'actions',
            cell: (data) => {
              const orderStatus = data.row.original.order?.status;

              const [isShowEditDialog, setIsShowEditDialog] = useState(false);

              const editForm = useForm<Transaction>({
                refineCoreProps: {
                  resource: resourceNames.transaction,
                  action: 'edit',
                  redirect: false,
                  errorNotification: (error) => {
                    return {
                      message: error?.message || 'An error occurred',
                      type: 'error',
                    };
                  },
                  onMutationSuccess: () => setIsShowEditDialog(false),
                  id: data.row.original.id,
                  metaData: {
                    fields: ['transactionCharge'] as any[],
                  },
                },
              });

              return (
                <div className="flex flex-row gap-2">
                  <Dialog open={isShowEditDialog} onOpenChange={setIsShowEditDialog}>
                    <DialogTrigger asChild>
                      <Button
                        variant={'solid'}
                        size="sm"
                        className="gap-2 min-w-[6rem]"
                        disabled={
                          orderStatus !== OrderStatus.Pending && 
                          orderStatus !== OrderStatus.Created && 
                          orderStatus !== OrderStatus.PendingOpsConfirmation
                        }
                      >
                        Edit
                      </Button>
                    </DialogTrigger>
                    <DialogPortal>
                      <DialogOverlay />
                      <DialogContent>
                        <DialogTitle>Edit Transaction</DialogTitle>
                        <div className="lg:min-w-full">
                          <div className="py-3">
                            <DynamicForm<Transaction>
                              form={editForm}
                              defaultValues={{
                                transactionCharge: data.row.original.transactionCharge,
                              }}
                              controls={{
                                components: {
                                  transactionCharge: {
                                    type: 'number',
                                    config: {
                                      label: 'Transaction Charge',
                                    },
                                  },
                                },
                              }}
                              submitText={false}
                              id={'edit-transaction-dialog'}
                              inferSections={false}
                              type={'edit'}
                            />
                          </div>
                          <div className="flex flex-row-reverse gap-3 pt-2 border-t">
                            <Button
                              type="submit"
                              variant="solid"
                              loading={editForm.refineCore.mutationResult.isLoading}
                              disabled={editForm.refineCore.formLoading}
                              form={'edit-transaction-dialog'}
                            >
                              Save
                            </Button>
                            <Button
                              onClick={() => setIsShowEditDialog(false)}
                              disabled={
                                editForm.refineCore.formLoading ||
                                editForm.refineCore.mutationResult.isLoading
                              }
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </DialogPortal>
                  </Dialog>
                </div>
              );
            },
          },
        ],
        getFilterConfig: ({ id }) => ({
          permanent: [
            {
              field: 'orderId',
              operator: 'eq',
              value: id,
            },
          ],
        }),
      }),
      defineRelatedResourceSection<Payment>({
        relatedResourceName: resourceNames.payment,
        fields: ['id'],
        relatedResourceFields: [
          'id',
          'referenceNo',
          'amount',
          'paymentMethod',
          'currency',
          'remarks',
          'paymentProof',
        ],
        title: 'Payments',
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        columns: () => [
          {
            id: 'referenceNo',
            header: 'Reference Number',
            accessorKey: 'referenceNo',
          },
          {
            id: 'currency',
            header: 'Currency',
            accessorKey: 'currency',
          },
          {
            id: 'amount',
            header: 'Amount',
            accessorKey: 'amount',
            cell: (data) => {
              const amount = data.cell.getValue<number>();
              return formatNumber(amount);
            },
          },
          {
            id: 'paymentMethod',
            header: 'Payment Method',
            accessorKey: 'paymentMethod',
            cell: (data) => {
              const paymentMethod = data.cell.getValue<PaymentMethod>();
              return sentenceCase(paymentMethod);
            },
          },
          {
            id: 'remarks',
            header: 'Remarks',
            accessorKey: 'remarks',
          },
          {
            id: 'paymentProof',
            header: 'Payment Proof',
            accessorKey: 'paymentProof',
            cell: (data) => {
              const paymentProof = data.cell.getValue<string>();
              if (!paymentProof) return 'No payment proof';
              return (
                <Dialog>
                  <div className="flex gap-2">
                    <DialogTrigger asChild>
                      <Button variant={'solid'} size="sm" className="gap-2">
                        <HiOutlineEye className="h-4 w-4" />
                        View
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-h-[80vh] max-w-[60vw] w-[fit-content] overflow-y-auto">
                      <ImageViewer src={paymentProof} alt="Payment Proof" />
                    </DialogContent>
                    <Button
                      variant={'solid'}
                      size="sm"
                      className="gap-2"
                      onClick={() => handleDownload(paymentProof, data.row.original.referenceNo)}
                    >
                      <HiOutlineDownload className="h-4 w-4" />
                      Download
                    </Button>
                  </div>
                </Dialog>
              );
            },
          },
        ],
        getFilterConfig: ({ id }) => ({
          permanent: [
            {
              field: 'orderId',
              operator: 'eq',
              value: id,
            },
          ],
        }),
      }),
      defineRelatedResourceSection<Signatory>({
        relatedResourceName: resourceNames.signatory,
        fields: ['id', { signatories: ['id'] }],
        relatedResourceFields: [
          { authorisedSignatory: ['email'] },
          'authorisedSignatoryId',
          'status',
        ],
        title: 'Signatories',
        shouldRender: ({ resource }) => !!resource?.signatories?.length,
        filterConfig: {
          collapsible: 'expand-by-default',
        },
        columns: () => [
          {
            id: 'authorisedSignatory.email',
            header: 'Authoriser Email',
            accessorKey: 'authorisedSignatory.email',
          },
          {
            id: 'status',
            header: 'Status',
            accessorKey: 'status',
            cell: (data) => {
              const status = data.cell.getValue<SignatoryStatus>();
              return <StatusField status={formatSignatoryStatus(status)} label={status} />;
            },
          },
          {
            id: 'authorisedSignatoryId',
            header: 'Signature',
            accessorKey: 'authorisedSignatoryId',
            cell: (data) => {
              const authorisedSignatoryId = data.cell.getValue<string>();
              if (!authorisedSignatoryId) return 'No Signature';

              const { data: authorisedSignatory } = useOne<AuthorisedSignatory>({
                resource: resourceNames.authorisedSignatory,
                id: authorisedSignatoryId,
                queryOptions: {
                  enabled: !!authorisedSignatoryId,
                },
                meta: {
                  fields: ['attachment'],
                },
              });

              if (!authorisedSignatory?.data?.attachment) return 'No Signature';

              return (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant={'solid'} size="sm" className="gap-2">
                      <HiOutlineEye className="h-4 w-4" />
                      View
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <ImageViewer src={authorisedSignatory?.data?.attachment} alt="Signature" />
                  </DialogContent>
                </Dialog>
              );
            },
          },
        ],
        getFilterConfig: ({ id }) => ({
          permanent: [
            {
              field: 'orderId',
              operator: 'eq',
              value: id,
            },
          ],
        }),
      }),
    ],
  }),
});
